import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { ChevronLeft, ChevronRight, Clock3, Search } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useRecentRowKeys, type TimeAgreement, type TimeTaskOption } from './api';

/** Row identity: one timesheet row / timer target per agreement × task combination. */
export function rowKeyOf(agreementId: string, taskId: string | null): string {
  return `${agreementId}:${taskId ?? ''}`;
}

// ---------------------------------------------------------------------------
// Recent picks — a daily user should not re-search an alphabetical list every
// morning. Stored locally (the API has no per-user "frequently used" concept).
// ---------------------------------------------------------------------------

const RECENT_KEY = 'financials-time-recent-picks';
const RECENT_LIMIT = 5;

function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberPick(agreementId: string, taskId: string | null): void {
  try {
    const key = rowKeyOf(agreementId, taskId);
    const next = [key, ...readRecent().filter((k) => k !== key)].slice(0, RECENT_LIMIT);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* private mode — recents just won't persist */
  }
}

// ---------------------------------------------------------------------------

export interface AgreementTaskPickerProps {
  agreements: TimeAgreement[];
  /**
   * Whose recent work to suggest. Without it the "Recent" list is whatever this
   * browser happens to remember; with it the server fills the gap on a new
   * device.
   */
  personId?: string | null;
  /** rowKeyOf(agreementId, taskId) combos to hide (already on the timesheet). */
  excludeKeys?: Set<string>;
  onPick: (agreement: TimeAgreement, task: TimeTaskOption | null) => void;
  onClose: () => void;
  /**
   * The element that toggles this panel. Outside-click close ignores it so the
   * trigger's own onClick can close the panel instead of instantly reopening it
   * (mousedown would close, then the click would toggle it back on).
   */
  triggerRef?: RefObject<HTMLElement>;
  /** Where the panel hangs relative to its trigger. */
  placement?: 'above-left' | 'below-right';
  /** Copy for the "nothing left to pick" case. */
  exhaustedLabel?: string;
  /**
   * Render in flow instead of as a popover (used inside the day view's entry
   * modal and inline at the bottom of the week grid, so one searchable cascade
   * serves the whole feature).
   */
  inline?: boolean;
  /**
   * Close on Escape / outside click. Defaults to true for popovers and false
   * inline — the day view's modal owns both gestures via Radix. The week grid
   * renders inline *and* owns them itself, so it opts back in.
   */
  dismissable?: boolean;
  /** Scroll the panel into view on open (inline pickers can open off-screen). */
  revealOnOpen?: boolean;
}

/** One selectable line in the flattened, keyboard-navigable option list. */
interface Option {
  id: string;
  agreement: TimeAgreement;
  /** Set on task rows and on recents that carry a task. */
  task: TimeTaskOption | null;
  /** Recents render their client + agreement inline rather than under a group. */
  recent?: boolean;
}

/**
 * Searchable client → agreement → task cascade. Shared by the week grid's
 * "Add row" button, the topbar timer's "Start timer" popover, and the day
 * view's entry modal.
 */
export function AgreementTaskPicker({
  agreements,
  personId = null,
  excludeKeys,
  onPick,
  onClose,
  triggerRef,
  placement = 'above-left',
  exhaustedLabel = 'Every agreement and task is already on this timesheet.',
  inline = false,
  dismissable = !inline,
  revealOnOpen = false,
}: AgreementTaskPickerProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TimeAgreement | null>(null);
  const [taskQuery, setTaskQuery] = useState('');
  const [active, setActive] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Local picks lead (they are this browser's freshest signal); the server's
  // recent rows fill in behind them so a new device is never empty.
  const serverRecent = useRecentRowKeys(personId);
  const recent = useMemo(() => {
    const merged: string[] = [];
    // `recent` spans 90 days, `rows` only the last timesheet — and the last
    // timesheet is usually the one on screen, whose combos are excluded. Take
    // both so the group survives that exclusion.
    const serverKeys = [
      ...(serverRecent.data?.recent ?? []),
      ...(serverRecent.data?.rows ?? []),
    ].map((row) => rowKeyOf(row.agreement.id, row.taskId));
    for (const key of [...readRecent(), ...serverKeys]) {
      if (!merged.includes(key)) merged.push(key);
    }
    // Kept generous here; `recentOptions` trims to RECENT_LIMIT after the
    // already-on-the-timesheet combos have been filtered out.
    return merged.slice(0, RECENT_LIMIT * 4);
  }, [serverRecent.data]);

  useEffect(() => {
    if (!revealOnOpen) return;
    panelRef.current?.scrollIntoView({ block: 'nearest' });
  }, [revealOnOpen]);

  useEffect(() => {
    if (!dismissable) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef?.current?.contains(target)) return; // let the trigger toggle
      if (panelRef.current && !panelRef.current.contains(target)) onClose();
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, triggerRef, dismissable]);

  /** Tasks of an agreement that are still pickable ("no task" counts as one option). */
  const openTasksOf = useCallback(
    (agreement: TimeAgreement): TimeTaskOption[] =>
      (agreement.tasks ?? []).filter((t) => !excludeKeys?.has(rowKeyOf(agreement.id, t.id))),
    [excludeKeys],
  );

  const hasOpenCombo = useCallback(
    (agreement: TimeAgreement): boolean =>
      (agreement.tasks ?? []).length > 0
        ? openTasksOf(agreement).length > 0
        : !excludeKeys?.has(rowKeyOf(agreement.id, null)),
    [excludeKeys, openTasksOf],
  );

  const pick = (agreement: TimeAgreement, task: TimeTaskOption | null) => {
    rememberPick(agreement.id, task?.id ?? null);
    onPick(agreement, task);
  };

  // --- agreement step ---------------------------------------------------------

  /** Client groups matching the query. Search covers client, agreement AND task names. */
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const terms = q.split(/\s+/).filter(Boolean);
    const matches = (agreement: TimeAgreement) => {
      if (terms.length === 0) return true;
      const haystack = [
        agreement.name,
        agreement.client.name,
        ...(agreement.tasks ?? []).map((t) => t.name),
      ]
        .join(' ')
        .toLowerCase();
      return terms.every((term) => haystack.includes(term));
    };
    const list = agreements.filter((p) => hasOpenCombo(p) && matches(p));
    const byClient = new Map<string, TimeAgreement[]>();
    for (const agreement of list) {
      const group = byClient.get(agreement.client.name) ?? [];
      group.push(agreement);
      byClient.set(agreement.client.name, group);
    }
    return [...byClient.entries()];
  }, [agreements, query, hasOpenCombo]);

  /** Recent agreement × task combos that are still pickable (no query only). */
  const recentOptions = useMemo((): Option[] => {
    if (query.trim() || recent.length === 0) return [];
    const byId = new Map(agreements.map((p) => [p.id, p]));
    const options: Option[] = [];
    for (const key of recent) {
      const [agreementId, taskId] = [key.slice(0, key.lastIndexOf(':')), key.slice(key.lastIndexOf(':') + 1)];
      const agreement = byId.get(agreementId);
      if (!agreement) continue;
      if (excludeKeys?.has(key)) continue;
      const task = taskId ? (agreement.tasks ?? []).find((t) => t.id === taskId) ?? null : null;
      if (taskId && !task) continue;
      options.push({ id: `recent:${key}`, agreement, task, recent: true });
    }
    return options.slice(0, RECENT_LIMIT);
  }, [recent, agreements, excludeKeys, query]);

  const agreementOptions = useMemo(
    (): Option[] =>
      groups.flatMap(([, items]) =>
        items.map((agreement) => ({ id: `agreement:${agreement.id}`, agreement, task: null })),
      ),
    [groups],
  );

  const taskOptions = useMemo(
    (): Option[] =>
      selected
        ? openTasksOf(selected)
            .filter((t) => t.name.toLowerCase().includes(taskQuery.trim().toLowerCase()))
            .map((task) => ({ id: `task:${task.id}`, agreement: selected, task }))
        : [],
    [selected, taskQuery, openTasksOf],
  );

  const options = selected ? taskOptions : [...recentOptions, ...agreementOptions];

  // Keep the highlight in range and scrolled into view as the list changes.
  useEffect(() => setActive(0), [query, taskQuery, selected]);
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, options.length]);

  const choose = (option: Option) => {
    if (option.task || option.recent) {
      pick(option.agreement, option.task);
      return;
    }
    const tasks = openTasksOf(option.agreement);
    if ((option.agreement.tasks ?? []).length === 0) {
      pick(option.agreement, null); // agreement offers no tasks → single row
      return;
    }
    if (tasks.length === 1) {
      pick(option.agreement, tasks[0]); // only one task left → skip the second step
      return;
    }
    setSelected(option.agreement);
    setTaskQuery('');
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (options.length === 0 ? 0 : (i + 1) % options.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (options.length === 0 ? 0 : (i - 1 + options.length) % options.length));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(Math.max(0, options.length - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[active];
      if (option) choose(option);
    } else if (event.key === 'Backspace' && selected && taskQuery === '') {
      event.preventDefault();
      setSelected(null);
    }
    // Escape is deliberately not handled here. In the popover it closes the
    // panel (document listener above); inside the day view's modal Radix owns
    // it on the capture phase and closes the dialog — one predictable meaning.
    // Backspace (and the ← button) is how you leave the task step.
  };

  const listboxId = 'time-picker-listbox';
  const optionRow = (option: Option, index: number) => {
    const isActive = index === active;
    // Recents name the agreement (their second line carries client · task);
    // inside the task step the option IS the task.
    const label = !option.recent && option.task ? option.task.name : option.agreement.name;
    const billable = option.task ? option.task.billable : option.agreement.billBy !== 'NONE';
    const drilldown = !option.task && !option.recent && (option.agreement.tasks ?? []).length > 1;
    return (
      <button
        key={option.id}
        id={`${listboxId}-${index}`}
        role="option"
        aria-selected={isActive}
        data-active={isActive}
        type="button"
        tabIndex={-1}
        onMouseEnter={() => setActive(index)}
        onClick={() => choose(option)}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm text-secondary transition-colors',
          isActive ? 'bg-accent/10' : 'hover:bg-grey-100',
        )}
      >
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">{label}</span>
            {billable && (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-success"
                title="Billable"
                aria-hidden
              />
            )}
          </span>
          {option.recent && (
            <span className="truncate text-caption text-grey-500">
              {option.agreement.client.name}
              {option.task ? ` · ${option.task.name}` : ''}
            </span>
          )}
        </span>
        {drilldown && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-grey-300" aria-hidden />}
      </button>
    );
  };

  // Options are rendered grouped, but their indices must match `options`.
  let cursor = recentOptions.length;

  return (
    <div
      ref={panelRef}
      className={cn(
        'rounded-xl border border-grey-200 bg-white',
        inline
          ? 'w-full shadow-none'
          : cn(
              'absolute z-40 w-80 max-w-[calc(100vw-2rem)] shadow-elevated',
              placement === 'above-left' ? 'bottom-full left-0 mb-2' : 'right-0 top-full mt-2',
            ),
      )}
    >
      {selected && (
        <div className="flex items-center gap-2 border-b border-grey-100 px-3 py-2">
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            <span className="sr-only">Back to agreements</span>
          </button>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-secondary">{selected.name}</p>
            <p className="truncate text-caption text-grey-500">{selected.client.name} · pick a task</p>
          </div>
        </div>
      )}

      <div className="relative border-b border-grey-100 p-2">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-grey-400"
          aria-hidden
        />
        <input
          autoFocus
          role="combobox"
          aria-expanded
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={options[active] ? `${listboxId}-${active}` : undefined}
          aria-label={selected ? 'Search tasks' : 'Search agreements, clients or tasks'}
          value={selected ? taskQuery : query}
          onChange={(e) => (selected ? setTaskQuery(e.target.value) : setQuery(e.target.value))}
          onKeyDown={onListKeyDown}
          placeholder={selected ? 'Search tasks…' : 'Search agreements, clients or tasks…'}
          className="w-full rounded-lg border-0 bg-grey-50 py-2 pl-8 pr-3 text-sm text-secondary placeholder:text-grey-400 focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </div>

      <div className="relative">
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={selected ? 'Tasks' : 'Agreements'}
          className="max-h-72 overflow-y-auto p-1.5"
        >
          {options.length === 0 &&
            (() => {
              const term = (selected ? taskQuery : query).trim();
              // A dead end needs a way out. "Nothing matches your search." on
              // its own leaves the only recovery to guesswork — clear it, widen
              // it, or find out who can add the agreement you are looking for.
              if (!term) {
                return (
                  <div className="px-3 py-6 text-center">
                    <p className="text-body-sm text-grey-500">{exhaustedLabel}</p>
                    {selected && (
                      <button
                        type="button"
                        onClick={() => setSelected(null)}
                        className="mt-2 text-caption font-semibold text-accent underline-offset-2 hover:underline"
                      >
                        Back to all agreements
                      </button>
                    )}
                  </div>
                );
              }
              return (
                <div className="px-3 py-5 text-center">
                  <p className="text-body-sm text-secondary">
                    No {selected ? 'task' : 'agreement'} matches{' '}
                    <span className="font-semibold">“{term}”</span>
                  </p>
                  <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => (selected ? setTaskQuery('') : setQuery(''))}
                      className="rounded-md border border-grey-200 px-2 py-1 text-caption font-semibold text-secondary transition-colors hover:bg-grey-100"
                    >
                      Clear search
                    </button>
                    {selected && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(null);
                          setTaskQuery('');
                        }}
                        className="rounded-md border border-grey-200 px-2 py-1 text-caption font-semibold text-secondary transition-colors hover:bg-grey-100"
                      >
                        Search all agreements
                      </button>
                    )}
                  </div>
                  <p className="mt-3 text-caption leading-snug text-grey-500">
                    {selected
                      ? 'Only the tasks this agreement offers are listed. An agreement admin adds tasks in Agreements → this agreement.'
                      : 'Search covers client, agreement and task names. Missing an agreement? An admin can add it in Agreements — or it may be archived, or not assigned to you.'}
                  </p>
                </div>
              );
            })()}

          {selected
            ? taskOptions.map((option, index) => optionRow(option, index))
            : (
                <>
                  {recentOptions.length > 0 && (
                    <div className="mb-1">
                      <p className="flex items-center gap-1 px-3 pb-0.5 pt-1.5 text-caption font-bold uppercase tracking-wider text-grey-400">
                        <Clock3 className="h-3 w-3" aria-hidden /> Recent
                      </p>
                      {recentOptions.map((option, index) => optionRow(option, index))}
                    </div>
                  )}
                  {groups.map(([clientName, items]) => {
                    const start = cursor;
                    cursor += items.length;
                    return (
                      <div key={clientName} className="mb-1">
                        <p className="px-3 pb-0.5 pt-1.5 text-caption font-bold uppercase tracking-wider text-grey-400">
                          {clientName}
                        </p>
                        {items.map((agreement, i) =>
                          optionRow({ id: `agreement:${agreement.id}`, agreement, task: null }, start + i),
                        )}
                      </div>
                    );
                  })}
                </>
              )}
        </div>
        {/* Scroll cue: the list clips mid-group without it. Never over the
            no-results state — it would fade out the recovery copy. */}
        {options.length > 0 && (
          <div
            className="pointer-events-none absolute inset-x-1 bottom-0 h-6 rounded-b-xl bg-gradient-to-t from-white to-transparent"
            aria-hidden
          />
        )}
      </div>

      <p className="hidden border-t border-grey-100 px-3 py-1.5 text-caption text-grey-400 sm:block">
        ↑↓ to move · Enter to pick{selected ? ' · Backspace to go back' : ''}
      </p>
    </div>
  );
}
